/**
 * Shield impact: a hexagon flares on the barrier exactly where the shot came from.
 *
 * There is no shield PROP in shield mode — the mode is a stance, and this flare is the only
 * thing that shows it working. So the hex has to carry the whole read: it appears on the
 * surface of an implied bubble around the player, facing the attacker, and fades in ~0.35s.
 *
 * Purely cosmetic and local: the absorption itself is the server's mitigation plus the energy
 * the owner bills itself (see main.ts shieldSoak).
 */
import * as THREE from "three";

/** Flare lifetime (s). Short and sharp — an impact, not a pulse. Settable so it can be tuned
 *  live (and so a probe can hold a flare open long enough to photograph). */
const LIFE = 0.36;
/** Radius of the implied barrier the hexes sit on (m from the player's centre). */
const SHELL = 1.15;
const POOL = 8;
const _camPos = new THREE.Vector3();

interface Hex {
  mesh: THREE.Mesh;
  /** Direction from the player toward the attacker (planet-local is not needed — this lives one frame). */
  dir: THREE.Vector3;
  t: number;
}

export class ShieldHex {
  /** Seconds a flare lives. See LIFE. */
  life = LIFE;
  private readonly hexes: Hex[] = [];
  private readonly group = new THREE.Group();

  constructor(scene: THREE.Scene, accent: THREE.Color) {
    this.group.renderOrder = 3;
    scene.add(this.group);
    // A flat hexagon (6-gon) plus a brighter rim, both additive: the plate reads as energy, and
    // depthWrite off keeps it from carving into the avatar it hugs.
    const plate = new THREE.CircleGeometry(0.42, 6);
    const rim = new THREE.RingGeometry(0.38, 0.44, 6);
    for (let i = 0; i < POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: accent.clone().lerp(new THREE.Color(0xffffff), 0.35),
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        // Draw OVER the world, including the avatar. A hit from behind must still be visible —
        // the whole job of the flare is telling you which way you're being shot from, and one
        // hidden behind your own back tells you nothing.
        depthTest: false,
        side: THREE.DoubleSide,
        fog: false,
      });
      const mesh = new THREE.Mesh(plate, mat);
      const edge = new THREE.Mesh(rim, mat);
      mesh.add(edge);
      mesh.visible = false;
      this.group.add(mesh);
      // t = Infinity, not LIFE: "free" is `t >= life`, and seeding it with the CONSTANT means a
      // tuned-up `life` makes every slot look busy — the pool then collapses to one hex and
      // simultaneous hits overwrite each other.
      this.hexes.push({ mesh, dir: new THREE.Vector3(0, 0, 1), t: Infinity });
      void i;
    }
  }

  private colorHex = -1;

  /** Tint the flare (the shield is the pilot's KI — it matches the aura color). */
  setColor(hex: number): void {
    if (hex === this.colorHex) return;
    this.colorHex = hex;
    for (const h of this.hexes) {
      (h.mesh.material as THREE.MeshBasicMaterial).color
        .setHex(hex).lerp(new THREE.Color(0xffffff), 0.35);
    }
  }

  /**
   * Flare a hex. `fromWorld` is where the damage came FROM and `atWorld` is the player — the hex
   * lands on the line between them, so a hit from behind flares behind you. A hit with no known
   * origin (a hazard, a bolt whose shooter is gone) still deserves a flare: pass the player's own
   * forward and it shows in front rather than showing nothing.
   */
  hit(atWorld: THREE.Vector3, fromWorld: THREE.Vector3): void {
    const hex = this.hexes.find((h) => h.t >= this.life) ?? this.hexes[0];
    hex.dir.copy(fromWorld).sub(atWorld);
    if (hex.dir.lengthSq() < 1e-6) hex.dir.set(0, 0, 1);
    hex.dir.normalize();
    hex.t = 0;
    hex.mesh.visible = true;
  }

  /** `playerRender` is the player's position in RENDER space (world − floating origin). */
  update(dt: number, playerRender: THREE.Vector3, camera: THREE.Camera): void {
    for (const hex of this.hexes) {
      if (hex.t >= this.life) continue;
      hex.t += dt;
      const k = Math.min(1, hex.t / this.life);
      if (k >= 1) {
        hex.mesh.visible = false;
        continue;
      }
      // POSITION carries the direction: the hex sits on the barrier shell, on the side the shot
      // came from. ORIENTATION faces the camera — a plate turned to face the attacker goes edge-on
      // (i.e. invisible) for any hit that isn't coming straight at the viewer, which is most of them.
      hex.mesh.position.copy(playerRender).addScaledVector(hex.dir, SHELL);
      hex.mesh.lookAt(camera.getWorldPosition(_camPos));
      // Snap in bright and hard, then bleed out — an impact, not a pulse. It also swells a little,
      // which is what makes it read as absorbed force rather than a decal.
      const mat = hex.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (1 - k) * (1 - k) * 0.85;
      const swell = 1 + k * 0.35;
      hex.mesh.scale.setScalar(swell);
    }
  }

  dispose(): void {
    for (const hex of this.hexes) {
      hex.mesh.geometry.dispose();
      (hex.mesh.material as THREE.Material).dispose();
      hex.mesh.children.forEach((c) => (c as THREE.Mesh).geometry.dispose());
    }
    this.group.removeFromParent();
  }
}
